import { HEADER_REVEAL_EVENT } from '@lib/util/useScrollDirection';
import {
  RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useSyncExternalStore,
} from 'react';
import { isApplePlatform } from 'utils';

export type UseSearchHotkeyOptions = {
  inputRef: RefObject<HTMLInputElement | null>;
  onActivate?: () => void;
};

export type SearchHotkey = {
  shortcutKeys: string[] | null;
  ariaKeyShortcuts?: string;
};

const subscribeNoop = () => () => {};

// The header swaps its plain form for the connected box in one commit. With a
// passive effect the old listener's ref is already empty and the new listener
// is not yet attached until effects flush, so a press in between is lost; on
// a busy main thread that gap is long enough for a Cypress keystroke to land
// in it. A layout effect moves the listener in the same commit as the input.
// The server runs neither, and React 18 warns about useLayoutEffect there.
const useIsomorphicLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect;

// Ctrl+K is kill-line in macOS text fields, so each platform answers to its
// own modifier only. Layouts whose K key types a non-Latin letter (Cyrillic,
// Greek) fall back to the physical key; a Latin remap such as Dvorak keeps
// its own K.
export const isSearchHotkey = (
  event: KeyboardEvent,
  isApple: boolean
): boolean => {
  if (event.isComposing || event.repeat || event.altKey || event.shiftKey) {
    return false;
  }
  const modifier = isApple
    ? event.metaKey && !event.ctrlKey
    : event.ctrlKey && !event.metaKey;
  if (!modifier) {
    return false;
  }
  return (
    event.key.toLowerCase() === 'k' ||
    (!/^[a-z]$/i.test(event.key) && event.code === 'KeyK')
  );
};

// OneTrust keeps its closed preference center in the DOM as an aria-modal
// dialog under a display:none wrapper, so only a rendered modal counts.
// Browsers without checkVisibility (Safari < 17.4) report no client rects for
// an element in a display:none subtree.
const isRendered = (element: Element) =>
  element.checkVisibility?.() ?? element.getClientRects().length > 0;

export const isBehindModal = (input: HTMLElement): boolean =>
  Boolean(input.closest('[aria-hidden="true"], [inert]')) ||
  Array.from(document.querySelectorAll('[aria-modal="true"]')).some(isRendered);

export const useSearchHotkey = ({
  inputRef,
  onActivate,
}: UseSearchHotkeyOptions): SearchHotkey => {
  const isApple = useSyncExternalStore(
    subscribeNoop,
    () => isApplePlatform(navigator),
    () => null
  );

  const onActivateRef = useRef(onActivate);
  onActivateRef.current = onActivate;

  useIsomorphicLayoutEffect(() => {
    if (isApple === null) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || !isSearchHotkey(event, isApple)) {
        return;
      }
      const input = inputRef.current;
      if (!input || input.disabled || isBehindModal(input)) {
        return;
      }
      event.preventDefault();
      if (document.activeElement === input) {
        input.select();
        input.dispatchEvent(new Event(HEADER_REVEAL_EVENT, { bubbles: true }));
        onActivateRef.current?.();
        return;
      }
      input.focus({ preventScroll: true });
      input.select();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isApple, inputRef]);

  if (isApple === null) {
    return { shortcutKeys: null, ariaKeyShortcuts: undefined };
  }
  return isApple
    ? { shortcutKeys: ['⌘', 'K'], ariaKeyShortcuts: 'Meta+K' }
    : { shortcutKeys: ['Ctrl', 'K'], ariaKeyShortcuts: 'Control+K' };
};
